<template>
  <main class="four-in-a-row-page" :class="appStore.theme">
    <section class="game-stage">
      <aside v-if="gamePlayers[0]" class="player-card red" :style="{ '--player-colour': gamePlayers[0].playerColour }">
        <span class="disc"></span>
        <span class="connection-dot" :class="{ offline: gamePlayers[0].connected === false, away: gamePlayers[0].connected !== false && !gamePlayers[0].inGame }" :style="{ backgroundColor: gamePlayers[0].connected === false ? '#ff4d5e' : gamePlayers[0].inGame === true ? '#40dc74' : '#ffad32' }" />
        <span class="player-name">{{ playerName(gamePlayers[0]) }}</span>
        <span class="controller-hint">{{ t('four_in_a_row.uses_colour', { colour: t('four_in_a_row.red') }) }}</span>
      </aside>
      <section class="game-content">
        <h1>{{ t('four_in_a_row.title') }}</h1>
        <p class="instruction">{{ t('four_in_a_row.instruction') }}</p>
        <p v-if="!winner" class="turn-message">{{ turnMessage }}</p>
        <div class="board" :style="{ '--columns': columns }" :aria-label="t('four_in_a_row.board')">
          <template v-for="(row, rowIndex) in board" :key="rowIndex">
            <span v-for="(cell, columnIndex) in row" :key="columnIndex" class="slot" :class="cell || ''"><i></i></span>
          </template>
        </div>
      </section>
      <aside v-if="gamePlayers[1]" class="player-card yellow" :style="{ '--player-colour': gamePlayers[1].playerColour }">
        <span class="disc"></span>
        <span class="connection-dot" :class="{ offline: gamePlayers[1].connected === false, away: gamePlayers[1].connected !== false && !gamePlayers[1].inGame }" :style="{ backgroundColor: gamePlayers[1].connected === false ? '#ff4d5e' : gamePlayers[1].inGame === true ? '#40dc74' : '#ffad32' }" />
        <span class="player-name">{{ playerName(gamePlayers[1]) }}</span>
        <span class="controller-hint">{{ t('four_in_a_row.uses_colour', { colour: t('four_in_a_row.yellow') }) }}</span>
      </aside>
    </section>
    <section v-if="winner" class="result-overlay" :class="{ draw: winner === 'draw' }">
      <div v-if="winner !== 'draw'" class="fireworks" aria-hidden="true"><i v-for="spark in 48" :key="spark" :style="confettiStyle(spark)"></i></div>
      <div class="result-card">
        <div class="result-icon">{{ winner === 'draw' ? '🤝' : '🏆' }}</div>
        <h2>{{ resultTitle }}</h2>
        <p>{{ winner === 'draw' ? t('four_in_a_row.draw_subtitle') : t('four_in_a_row.win_subtitle') }}</p>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/appStore'
import eventBus from '@/plugins/eventBus'
import { useWebsocketStore } from '@/stores/websocketStore'
import { soundManager, SOUNDS } from '@/services/soundManager'

const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t } = useI18n()
const rows = 6
const columns = computed(() => Math.max(4, Number(appStore.room?.game?.gameplay?.controller?.options?.columns) || 7))
const emptyBoard = () => Array.from({ length: rows }, () => Array(columns.value).fill(''))
const board = ref(emptyBoard())
const turnIndex = ref(Number(appStore.room?.game?.roundStarterIndex) === 1 ? 1 : 0)
const winner = ref('')
const gamePlayers = computed(() => (appStore.room?.game?.players || []).slice(0, 2))
const activePlayer = computed(() => gamePlayers.value[turnIndex.value])
const colours = ['red', 'yellow']

function playerName(player) {
  return player?.displayName || player?.name || player?.email || t('four_in_a_row.player')
}
const turnMessage = computed(() => !activePlayer.value
  ? t('four_in_a_row.waiting')
  : t('four_in_a_row.turn', { player: playerName(activePlayer.value), colour: t(`four_in_a_row.${colours[turnIndex.value]}`) }))
const resultTitle = computed(() => {
  if (winner.value === 'draw') return t('four_in_a_row.draw')
  return t('four_in_a_row.player_wins', { player: playerName(gamePlayers.value[colours.indexOf(winner.value)]) })
})
function winningMove(row, column, colour) {
  return [[0, 1], [1, 0], [1, 1], [1, -1]].some(([rowStep, columnStep]) => {
    let total = 1
    for (const direction of [-1, 1]) {
      let nextRow = row + rowStep * direction
      let nextColumn = column + columnStep * direction
      while (nextRow >= 0 && nextRow < rows && nextColumn >= 0 && nextColumn < columns.value && board.value[nextRow][nextColumn] === colour) {
        total++
        nextRow += rowStep * direction
        nextColumn += columnStep * direction
      }
    }
    return total >= 4
  })
}
function handleControl(control) {
  const column = Number(control?.value) - 1
  if (control?.type !== 'column' || winner.value || column < 0 || column >= columns.value) return
  const playerIndex = gamePlayers.value.findIndex((player) => player.id === control.userId)
  if (playerIndex === -1 || playerIndex !== turnIndex.value) return
  const row = [...Array(rows).keys()].reverse().find((index) => !board.value[index][column])
  if (row === undefined) return
  const colour = colours[playerIndex]
  board.value[row][column] = colour
  soundManager.play(SOUNDS.selection)
  if (winningMove(row, column, colour)) finishGame(colour)
  else if (board.value.every((boardRow) => boardRow.every(Boolean))) finishGame('draw')
  else turnIndex.value = turnIndex.value === 0 ? 1 : 0
}
function finishGame(result) {
  soundManager.stopAll(); soundManager.play(result === 'draw' ? SOUNDS.gameDraw : SOUNDS.winner)
  winner.value = result
  socketStore.send({ event: 'finished', data: { gameId: appStore.game?.id, result } })
}
function confettiStyle(spark) {
  return {
    '--x': `${(spark * 37) % 100}%`,
    '--delay': `${(spark % 12) * -0.18}s`,
    '--drift': `${((spark * 29) % 180) - 90}px`,
    '--colour': `hsl(${(spark * 43) % 360}, 90%, 62%)`,
  }
}
function restartRound() {
  board.value = emptyBoard()
  winner.value = ''
  turnIndex.value = Number(appStore.room?.game?.roundStarterIndex) === 1 ? 1 : 0
}
onMounted(() => {
  eventBus.on('game-control', handleControl)
  eventBus.on('restart-round', restartRound)
  socketStore.send({ event: 'playing', data: { gameId: appStore.game.id } })
})
onBeforeUnmount(() => {
  soundManager.stopAll()
  eventBus.off('game-control', handleControl)
  eventBus.off('restart-round', restartRound)
})
</script>

<style scoped>
.four-in-a-row-page { height: 100vh; padding: 2vh 3vw; box-sizing: border-box; overflow: hidden; background: #101218; color: #fff; }
.four-in-a-row-page.light { background: #f5f6fa; color: #1d2433; }
.game-stage { height: 100%; display: grid; grid-template-columns: minmax(150px, 1fr) minmax(440px, auto) minmax(150px, 1fr); align-items: center; gap: 2vw; }
.player-card { position: relative; display: grid; grid-template-columns: auto 1fr; column-gap: 1vw; align-items: center; padding: 2vh 1.5vw; border-radius: 18px; background: #202530; border: 3px solid var(--player-colour, #566070); }.disc { grid-row: span 2; width: clamp(2.2rem, 6vh, 3.8rem); aspect-ratio: 1; border-radius: 50%; background: #ef5350; box-shadow: inset 0 -.35rem #b71c1c; }.yellow .disc { background: #ffd54f; box-shadow: inset 0 -.35rem #f9a825; }
.player-name { font-size: clamp(1rem, 2.7vh, 1.6rem); font-weight: 700; }.connection-dot { position: absolute; top: .65rem; right: .65rem; width: .8rem; height: .8rem; border-radius: 50%; background: #40dc74; box-shadow: 0 0 .6rem #40dc74; }.connection-dot.away { background: #ffad32; box-shadow: 0 0 .6rem #ffad32; }.connection-dot.offline { background: #77808f; box-shadow: none; }.controller-hint { opacity: .72; font-size: clamp(.8rem, 1.8vh, 1.05rem); }.game-content { display: flex; flex-direction: column; align-items: center; text-align: center; }.game-content h1 { margin: 0; font-size: clamp(1.8rem, 4.5vh, 3rem); }.instruction { margin: 0; opacity: .75; font-size: clamp(.8rem, 2vh, 1.2rem); }.turn-message { margin: .5vh 0; min-height: 1.5rem; font-size: clamp(1rem, 2.6vh, 1.55rem); font-weight: 700; }
.board { display: grid; grid-template-columns: repeat(var(--columns), min(11vh, 10vw, 115px)); gap: min(1.1vh, 8px); padding: min(1.4vh, 12px); background: #2474d4; border-radius: 18px; box-shadow: 0 .7rem 0 #124789; }.slot { display: grid; place-items: center; aspect-ratio: 1; border-radius: 50%; background: #0c2340; box-shadow: inset 0 .25rem .5rem #0008; }.slot i { width: 82%; aspect-ratio: 1; border-radius: 50%; background: #f7f9ff; }.slot.red i { background: #ef5350; box-shadow: inset 0 -.35rem #b71c1c; }.slot.yellow i { background: #ffd54f; box-shadow: inset 0 -.35rem #f9a825; }.light .player-card { background: #fff; }.light .slot:not(.red):not(.yellow) i { background: #e7edf7; }.light .slot.red i { background: #ef5350; }.light .slot.yellow i { background: #ffd54f; }
.result-overlay { position: fixed; inset: 0; z-index: 50; display: flex; align-items: center; justify-content: center; overflow: hidden; background: rgba(4, 8, 20, .82); }.result-card { position: relative; z-index: 1; width: min(80vw, 680px); margin: auto; padding: clamp(2rem, 7vh, 5rem); border-radius: 2rem; text-align: center; color: #182333; background: #fff; box-shadow: 0 1.5rem 5rem #0008; }.result-icon { font-size: clamp(4rem, 12vh, 8rem); }.result-card h2 { margin: .5rem 0; font-size: clamp(2rem, 6vh, 4.5rem); }.result-card p { margin: 0; font-size: clamp(1rem, 2.5vh, 1.5rem); }.fireworks { position: absolute; inset: 0; }.fireworks i { position: absolute; left: var(--x); top: -5vh; width: .65rem; height: 1rem; border-radius: .18rem; background: var(--colour); animation: confetti-fall 3s linear infinite; animation-delay: var(--delay); }@keyframes confetti-fall { from { transform: translateX(0) rotate(0deg); opacity: 1; } to { transform: translateX(var(--drift)) translateY(115vh) rotate(780deg); opacity: .15; } }
@media (max-width: 700px) { .game-stage { grid-template-columns: 1fr; grid-template-rows: auto 1fr auto; gap: 1vh; }.player-card { padding: .6vh 2vw; }.board { grid-template-columns: repeat(var(--columns), min(10vh, 13vw)); } }
.connection-dot.offline { background: #ff4d5e; box-shadow: 0 0 .5rem #ff4d5e; }
</style>
